import { CustomTheme } from "@/core/context/CustomThemeProvider"
import { createComputedTheme, DEFAULT_LIGHT_THEME } from "@assets/style/appMuiTheme"
import {
  createCustomThemeCSSVariables,
  createThemeCSSVariablesfromJss,
} from "@assets/style/util/cssUtils"
import {
  jssPreset,
  StylesProvider as MuiStylesProvider,
  Theme,
  ThemeProvider as MuiThemeProvider,
} from "@material-ui/core/styles"
import { create as createJss } from "jss"
import {
  cloneElement,
  CSSProperties,
  FC,
  isValidElement,
  useEffect,
  useMemo,
} from "react"
import { JssProvider, ThemeProvider as JssThemeProvider } from "react-jss"

interface DiscoThemeProviderProps {
  theme: Theme
  // Force light theme
  unstyled?: boolean
  // Should initialize app theme
  shouldInitCSSTheme?: boolean
}

/** Get or create a <meta id={id}> tag at the top/bottom of the <head> */
function getOrCreateInsertionPoint(id: string, prepend: boolean) {
  let el = document.getElementById(id)
  if (el) return el
  el = document.createElement("meta")
  el.id = id
  if (prepend) {
    document.head.prepend(el)
  } else {
    document.head.append(el)
  }
  return el
}

type ComputedTheme = {
  jssTheme: Theme
  cssTheme: CSSProperties
}

/**
 * Make sure our custom JSS applies the same preset plugins as Mui JSS.
 * Provide custom insertionPoint elements so that MUI styles are always
 * higher in the DOM than our custom JSS styles. This way, our custom JSS
 * overrides MUI defaults even when the CSS selector specificity is the same.
 */
function createJssWithInsertionPoint(id: string, prepend = false) {
  // @ts-expect-error - jss package type slightly different from MUI. But works.
  return createJss({
    ...jssPreset(),
    insertionPoint: getOrCreateInsertionPoint(id, prepend),
  })
}

const muiJss = createJssWithInsertionPoint("mui-insertion-point", true)
const jss = createJssWithInsertionPoint("jss-insertion-point")

const DiscoThemeProvider: FC<DiscoThemeProviderProps> = (props) => {
  const { theme: themeProps, unstyled, shouldInitCSSTheme, children } = props
  const lightTheme = useMemo(() => createComputedTheme(DEFAULT_LIGHT_THEME), [])
  const theme = unstyled ? lightTheme : themeProps

  const { cssTheme, jssTheme } = useMemo<ComputedTheme>(() => {
    // Force mobile app theme if set in DOM from webview
    const mobileThemeJSON = document
      .querySelector('meta[name="mobile-theme"]')
      ?.getAttribute("content")

    if (!mobileThemeJSON)
      return {
        jssTheme: theme,
        cssTheme: createThemeCSSVariablesfromJss(theme),
      }

    try {
      const mobileTheme = JSON.parse(mobileThemeJSON) as CustomTheme
      return {
        // Compute the theme / CSS variables based on the mobile theme
        jssTheme: createComputedTheme(mobileTheme),
        cssTheme: createCustomThemeCSSVariables(mobileTheme),
      }
    } catch (e) {
      console.error("Failed to parse mobile theme", e)
      return {
        // Return the default light theme
        jssTheme: lightTheme,
        cssTheme: createCustomThemeCSSVariables(),
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme])

  // Set the community custom theme in the app
  useEffect(() => {
    if (!shouldInitCSSTheme) return

    document.body.setAttribute("data-theme", jssTheme.palette.type)

    /** Set the custom CSS variables to the body's style */
    for (const [property, value] of Object.entries(cssTheme)) {
      document.body.style.setProperty(property, value)
    }

    return () => {
      if (!shouldInitCSSTheme) return
      document.body.removeAttribute("data-theme")

      /** Remove the custom CSS variables from the body */
      for (const property of Object.keys(cssTheme)) {
        document.body.style.removeProperty(property)
      }
    }
  }, [jssTheme.palette.type, shouldInitCSSTheme, cssTheme])

  /** Don't render the theme provider if the children is undefined
   *  This removes the duplicate empty divs in the root
   */
  if (!children) return null

  const childrenProps = {
    // Add "data-theme" to the wrapper to override the custom theme
    "data-theme": unstyled ? "light" : undefined,
  }

  return (
    // @ts-expect-error - mui JSS type is subtly different from jss. But works...
    <MuiStylesProvider jss={muiJss}>
      <JssProvider jss={jss}>
        <JssThemeProvider theme={jssTheme}>
          <MuiThemeProvider theme={jssTheme}>
            {/** If children is a valid element, inject the cssTheme to the element to avoid any
             *   rendering issue caused by the additional css theme wrapper
             */}
            {isValidElement(children) ? (
              cloneElement(children, {
                ...children.props,
                ...childrenProps,
              })
            ) : (
              // Otherwise, wrap the children in a css theme wrapper
              <div {...childrenProps}>{children}</div>
            )}
          </MuiThemeProvider>
        </JssThemeProvider>
      </JssProvider>
    </MuiStylesProvider>
  )
}

export default DiscoThemeProvider
